import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from '@headlessui/react'
import {
  ArchiveBoxIcon,
  BookOpenIcon,
  ClipboardDocumentListIcon,
  FolderIcon,
  ChevronDownIcon,
  ClockIcon,
  EllipsisHorizontalIcon,
  PencilSquareIcon,
  HomeIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  RectangleStackIcon,
} from '@heroicons/react/24/outline'
import { useState } from 'react'
import { Link, NavLink } from 'react-router'
import type { ThreadRead } from '../../client'
import RenameDialog from '../chat/RenameDialog'
import { shortcutLabel } from '../../lib/shortcuts'
import AccountMenu from './AccountMenu'
import BrandSwitcher from './BrandSwitcher'
import HelpMenu from './HelpMenu'
import NotificationBell from './NotificationBell'
import { useChat } from '../../providers/chat/context'
import RecentsMenu from './RecentsMenu'

const NAV_ITEMS = [
  { to: '/', label: 'Home', icon: HomeIcon },
  { to: '/my-ads', label: 'My ads', icon: RectangleStackIcon },
  { to: '/competitor-research', label: 'Competitor research', icon: MagnifyingGlassIcon },
]

const ROW = 'flex h-10 w-full items-center overflow-hidden rounded-xl border-[0.5px] text-sm whitespace-nowrap transition-colors md:h-8'
const IDLE = 'border-transparent text-stone-700 hover:bg-black/5 hover:text-stone-950'
const ACTIVE = 'border-stone-300 bg-white text-stone-950 shadow-[0_1px_2px_rgba(28,25,23,0.06)]'
const MENU_ITEM =
  'flex h-10 w-full items-center gap-2.5 rounded-lg px-2.5 text-sm text-stone-700 transition-colors data-focus:bg-black/5 data-focus:text-stone-950 md:h-9'

type Props = {
  expanded: boolean
  onToggle: () => void
}

export default function Sidebar({ expanded, onToggle }: Props) {
  const { threads, activeId, openThread, newThread, archive } = useChat()
  const [renaming, setRenaming] = useState<ThreadRead | null>(null)

  return (
    <div className={`flex h-full flex-col px-3.5 py-2 ${expanded ? 'w-60' : 'w-15'}`}>
      <div className="flex h-10 items-center md:h-8">
        <BrandSwitcher expanded={expanded} />
        {expanded && (
          <button
            type="button"
            onClick={onToggle}
            aria-label="Toggle sidebar"
            data-tooltip={`Toggle sidebar (${shortcutLabel('B')})`}
            className="flex size-10 shrink-0 items-center justify-center rounded-lg text-stone-500 transition-colors hover:bg-black/5 hover:text-stone-900 md:size-8"
          >
            <SidebarIcon />
          </button>
        )}
      </div>

      <div className="mt-4 min-h-0 flex-1 overflow-y-auto [scrollbar-width:none]">
        <Link to="/" onClick={newThread} className={`${ROW} ${IDLE}`}>
          <RowIcon icon={PlusIcon} />
          New chat
          <kbd className="mr-2.5 ml-auto hidden font-sans text-[11px] text-stone-400 md:block">{shortcutLabel('K')}</kbd>
        </Link>

        <nav className="mt-3 flex flex-col gap-0.5">
          {NAV_ITEMS.map(({ to, label, icon }) => (
            <NavLink
              key={to}
              to={to}
              end
              onClick={to === '/' ? newThread : undefined}
              className={({ isActive }) =>
                // "/" also hosts the open conversation, which owns the highlight instead
                `${ROW} ${isActive && !(to === '/' && activeId) ? ACTIVE : IDLE}`
              }
            >
              <RowIcon icon={icon} />
              {label}
            </NavLink>
          ))}
        </nav>

        <nav className="mt-6 flex flex-col gap-0.5">
          <NavLink to="/brand-book" className={({ isActive }) => `${ROW} ${isActive ? ACTIVE : IDLE}`}>
            <RowIcon icon={BookOpenIcon} />
            Brand Book
          </NavLink>
          <NavLink to="/files" className={({ isActive }) => `${ROW} ${isActive ? ACTIVE : IDLE}`}>
            <RowIcon icon={FolderIcon} />
            Files
          </NavLink>
          <NavLink to="/workflows" className={({ isActive }) => `${ROW} ${isActive ? ACTIVE : IDLE}`}>
            <RowIcon icon={ClipboardDocumentListIcon} />
            Workflows
          </NavLink>
        </nav>

        {expanded && (
          <Disclosure as="section" defaultOpen className="mt-6">
            <div className="group relative">
              <DisclosureButton className="group/recents flex h-9 w-full items-center justify-between px-2 text-[13px] text-stone-500 transition-colors hover:text-stone-800 md:h-7">
                Recents
                <ChevronDownIcon className="size-3.5 -rotate-90 transition group-hover:opacity-0 group-data-open/recents:rotate-0 pointer-coarse:opacity-0" />
              </DisclosureButton>
              <RecentsMenu />
            </div>

            <DisclosurePanel as="ul" className="mt-1 flex flex-col gap-0.5">
              {threads.length === 0 && (
                <li className="px-2 py-1.5 text-[13px] text-stone-400">No conversations yet.</li>
              )}
              {threads.slice(0, 10).map((thread) => (
                <li key={thread.id} className="group/row relative">
                  <Link
                    to={`/c/${thread.id}`}
                    onClick={() => void openThread(thread.id)}
                    className={`${ROW} ${thread.id === activeId ? ACTIVE : IDLE} pr-9`}
                  >
                    <span className="flex size-8 shrink-0 items-center justify-center">
                      {thread.scheduled ? (
                        <ClockIcon
                          aria-label="Scheduled run"
                          className={`size-3.5 ${thread.id === activeId ? 'text-purple-600' : 'text-stone-400'}`}
                        />
                      ) : (
                        <span
                          className={`size-1.5 rounded-full ${
                            thread.id === activeId ? 'bg-purple-500 ring-4 ring-purple-500/15' : 'bg-stone-300'
                          }`}
                        />
                      )}
                    </span>
                    <span className="truncate">{thread.title || 'New conversation'}</span>
                  </Link>

                  <Menu>
                    <MenuButton
                      aria-label="Conversation options"
                      className="absolute top-1/2 right-1 flex size-7 -translate-y-1/2 items-center justify-center rounded-md text-stone-500 opacity-0 transition hover:bg-black/5 hover:text-stone-900 group-hover/row:opacity-100 focus-visible:opacity-100 data-open:opacity-100 pointer-coarse:opacity-100"
                    >
                      <EllipsisHorizontalIcon className="size-4" />
                    </MenuButton>
                    <MenuItems
                      anchor={{ to: 'bottom end', gap: 6 }}
                      className="z-50 w-40 rounded-xl border border-stone-200/80 bg-white/85 p-1 shadow-[0_2px_4px_rgba(28,25,23,0.04),0_18px_44px_-16px_rgba(28,25,23,0.22)] backdrop-blur-md"
                    >
                      <MenuItem>
                        <button
                          type="button"
                          onClick={() => setRenaming(thread)}
                          className={MENU_ITEM}
                        >
                          <PencilSquareIcon className="size-4 text-stone-500" />
                          Rename
                        </button>
                      </MenuItem>
                      <MenuItem>
                        <button
                          type="button"
                          onClick={() => void archive(thread.id)}
                          className={MENU_ITEM}
                        >
                          <ArchiveBoxIcon className="size-4 text-stone-500" />
                          Archive
                        </button>
                      </MenuItem>
                    </MenuItems>
                  </Menu>
                </li>
              ))}
            </DisclosurePanel>
          </Disclosure>
        )}
      </div>

      <div className={`flex gap-1 pt-2 ${expanded ? 'items-center' : 'flex-col-reverse'}`}>
        <AccountMenu />
        <NotificationBell />
        <HelpMenu />
      </div>

      {renaming && <RenameDialog thread={renaming} onClose={() => setRenaming(null)} />}
    </div>
  )
}

function RowIcon({ icon: Icon }: { icon: typeof HomeIcon }) {
  return (
    <span className="flex size-8 shrink-0 items-center justify-center">
      <Icon className="size-4" />
    </span>
  )
}

function SidebarIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden className="size-4">
      <rect x="3" y="4" width="18" height="16" rx="3" />
      <path d="M9 4v16" />
    </svg>
  )
}
